import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authAPI } from '../api';

const AuthContext = createContext(null);

// Default permissions by role - synced with backend DEFAULT_ROLE_PERMISSIONS
const DEFAULT_PERMISSIONS = {
  super_admin: {
    dashboard: ['ver'],
    simulacion: ['ver', 'crear', 'editar', 'eliminar', 'controlar_monitor'],
    escenarios: ['ver', 'crear', 'editar', 'eliminar', 'activar_desactivar', 'exportar'],
    evaluaciones: ['ver', 'crear', 'editar', 'eliminar', 'exportar'],
    reportes: ['ver', 'crear', 'exportar'],
    medicamentos: ['ver', 'crear', 'editar', 'eliminar'],
    acciones: ['ver', 'crear', 'editar', 'eliminar'],
    protocolos: ['ver', 'crear', 'editar', 'eliminar'],
    usuarios: ['ver', 'crear', 'editar', 'eliminar', 'activar_desactivar', 'asignar_niveles', 'asignar_tenant'],
    establecimientos: ['ver', 'crear', 'editar', 'eliminar', 'activar_desactivar'],
    roles: ['ver', 'crear', 'editar', 'eliminar'],
    niveles: ['ver', 'crear', 'editar', 'eliminar', 'asignar_niveles'],
    auditoria: ['ver', 'exportar']
  },
  admin: {
    dashboard: ['ver'],
    simulacion: ['ver', 'crear', 'controlar_monitor'],
    escenarios: ['ver', 'crear', 'editar', 'activar_desactivar'],
    evaluaciones: ['ver', 'crear', 'editar', 'exportar'],
    reportes: ['ver', 'crear', 'exportar'],
    medicamentos: ['ver', 'crear', 'editar'],
    acciones: ['ver', 'crear', 'editar'],
    protocolos: ['ver', 'crear', 'editar'],
    usuarios: ['ver', 'crear', 'editar', 'activar_desactivar', 'asignar_niveles'],
    establecimientos: ['ver'],
    roles: ['ver'],
    niveles: ['ver', 'asignar_niveles'],
    auditoria: ['ver']
  },
  instructor: {
    dashboard: ['ver'],
    simulacion: ['ver', 'crear', 'controlar_monitor'],
    escenarios: ['ver', 'crear', 'editar'],
    evaluaciones: ['ver', 'crear', 'editar'],
    reportes: ['ver'],
    medicamentos: ['ver'],
    acciones: ['ver'],
    protocolos: ['ver'],
    usuarios: ['ver'],
    establecimientos: [],
    roles: [],
    niveles: ['ver'],
    auditoria: []
  },
  alumno: {
    dashboard: ['ver'],
    simulacion: ['ver', 'crear'],
    escenarios: ['ver'],
    evaluaciones: ['ver'],
    reportes: ['ver'],
    medicamentos: [],
    acciones: [],
    protocolos: [],
    usuarios: [],
    establecimientos: [],
    roles: [],
    niveles: [],
    auditoria: []
  }
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [permissions, setPermissions] = useState({});
  const [loading, setLoading] = useState(true);

  // Load user from localStorage on mount
  useEffect(() => {
    const token = localStorage.getItem('token');
    const savedUser = localStorage.getItem('user');
    
    if (token && savedUser) {
      try {
        const userData = JSON.parse(savedUser);
        setUser(userData);
        // Set permissions from user data or defaults
        const userPerms = userData.permissions || DEFAULT_PERMISSIONS[userData.role] || {};
        setPermissions(userPerms);
      } catch (e) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
      }
    }
    setLoading(false);
  }, []);

  const login = async (email, password) => {
    const response = await authAPI.login({ email, password });
    const { access_token, user: userData } = response.data;
    
    localStorage.setItem('token', access_token);
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
    
    // Set permissions
    const userPerms = userData.permissions || DEFAULT_PERMISSIONS[userData.role] || {};
    setPermissions(userPerms);
    
    return userData;
  };

  const register = async (data) => {
    const response = await authAPI.register(data);
    const { access_token, user: userData } = response.data;
    
    localStorage.setItem('token', access_token);
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
    
    const userPerms = userData.permissions || DEFAULT_PERMISSIONS[userData.role] || {};
    setPermissions(userPerms);
    
    return userData;
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
    setPermissions({});
  };

  const refreshUser = async () => {
    try {
      const response = await authAPI.getMe();
      const userData = response.data;
      localStorage.setItem('user', JSON.stringify(userData));
      setUser(userData);
      const userPerms = userData.permissions || DEFAULT_PERMISSIONS[userData.role] || {};
      setPermissions(userPerms);
      return userData;
    } catch (error) {
      logout();
      throw error;
    }
  };

  // Permission check function
  const hasPermission = useCallback((module, action) => {
    if (!user) return false;
    if (user.role === 'super_admin') return true;
    
    const modulePerms = permissions[module] || [];
    return modulePerms.includes(action);
  }, [user, permissions]);

  // Check if user can access a module (at least 'ver' permission)
  const canAccess = useCallback((module) => {
    return hasPermission(module, 'ver');
  }, [hasPermission]);

  // Computed role checks
  const isSuperAdmin = user?.role === 'super_admin';
  const isAdmin = user?.role === 'admin' || isSuperAdmin;
  const isInstructor = user?.role === 'instructor' || isAdmin;
  const isAlumno = user?.role === 'alumno';
  const isPending = user?.status === 'pendiente';
  const isActive = user?.status === 'activo' || user?.is_active;
  const hasTenant = !!user?.institution_id;

  const value = {
    user,
    permissions,
    loading,
    login,
    register,
    logout,
    refreshUser,
    hasPermission,
    canAccess,
    isAuthenticated: !!user,
    isSuperAdmin,
    isAdmin,
    isInstructor,
    isAlumno,
    isPending,
    isActive,
    hasTenant,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
